import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import Button from '../../../../components/Button';
import { TextArea } from '../../../../components/TextArea/TextArea';

import { inAppCancellation as events } from '../../../../lib/analytics/events';
import { useZendeskCancellationTicket, useZendeskReplacementTicket } from '../hooks/useZendesk';
import CancelConfirmationModal from '../../components/CancelConfirmationModal/CancelConfirmationModal';
import { useLocation } from 'react-router-dom';
import { gqlTypes } from '../../../../types';
import { BaseInAppCancellationState } from '../util/types';
import ErrorScreen from '../../components/ErrorScreen/ErrorScreen';

interface ReplaceCollarState extends BaseInAppCancellationState {
  selectedCancelReason: gqlTypes.zendeskCancellationReason;
}

export default function ReplaceCollar() {
  const { t } = useTranslation();
  const { device } = usePurchaseSubscriptionContext();
  const location = useLocation<ReplaceCollarState>();
  const { selectedCancelReason, subscriptionCategory } = location.state;
  const [replaceReasonText, setReplaceReasonText] = useState('');

  const { mutation: replaceMutation, loading: replaceLoading } = useZendeskReplacementTicket({
    analyticsEvent: events.replacementTicketSubmitted,
    replacementReason: replaceReasonText,
    device,
    subscriptionCategory,
  });

  const { mutation: cancelMutation, loading: cancelLoading } = useZendeskCancellationTicket({
    analyticsEvent: events.cancelFromFreeMonthPage,
    selectedCancelReason,
    device,
    subscriptionCategory,
  });

  if (replaceLoading) {
    return replaceLoading;
  }
  if (cancelLoading) {
    return cancelLoading;
  }

  if (!device.subscription?.id) {
    return <ErrorScreen />;
  }

  const handleReplacementRequest = () => {
    if (!replaceReasonText || !device.subscription?.id) {
      throw new Error(t('something_went_wrong'));
    }
    replaceMutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription.id,
          replacementReason: replaceReasonText,
        },
      },
    });
  };

  const handleCancel = () => {
    cancelMutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription!.id,
          cancellationReason: selectedCancelReason,
          additionalText: undefined,
        },
      },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.replaceCollarBack({ petId: device.pet?.id, subscriptionCategory })}
          closeButtonAnalyticsEvent={() => events.replaceCollarClose({ petId: device.pet?.id, subscriptionCategory })}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.sectionHeaderContainer}>
            <h2 className={styles.sectionSubheader}>{t('replacement_for_your_collar')}</h2>
          </div>
          <div className={styles.content}>
            <p>
              {t(
                'exciting_news_you_have_the_opportunity_to_get_a_brand_new_s3_collar_replacement_tell_us_what_happened_to_your_current_collar',
              )}
            </p>
            <div className={styles.textArea}>
              <TextArea
                onChange={(value) => {
                  value && setReplaceReasonText(value);
                }}
                rows={12}
                placeholderText={t('type_your_response_here')}
              />
            </div>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            onClick={() => handleReplacementRequest()}
            className={styles.button}
            disabled={replaceReasonText.length === 0}
          >
            {t('submit')}
          </Button>
          <CancelConfirmationModal
            onContinue={() => handleCancel()}
            pageName={t('replace_collar')}
            petId={device.pet!.id}
          />
        </div>
      </div>
    </>
  );
}
